import type { FormEvent } from 'react';
import type { StageRailsLayout, StageRailsToolsMode } from '../hooks/useStageRails.ts';
import {
  SHORTCUT_FONT_MAX,
  SHORTCUT_FONT_MIN,
  SHORTCUT_FONT_STEP,
} from '../lib/shortcutSteps.ts';

export type StageRailsProps = {
  visible: boolean;
  toolsMode: StageRailsToolsMode;
  pinned: boolean;
  /** Landscape = left/right; portrait = top/bottom. */
  layout?: StageRailsLayout;
  /** Current subY 0–1 for the position slider. */
  subY: number;
  /** Current fontSizeRatio for the size slider. */
  fontSize: number;
  /** Shadowing center-line 0–1 (0.5 = natural). */
  centerOffset?: number;
  /** Show horizontal center tool (shadowing only). */
  showCenterControl?: boolean;
  /** Main / second track delay in ms. */
  mainDelayMs?: number;
  secondDelayMs?: number;
  /** True when video is paused — play button shows ▶. */
  paused: boolean;
  onSeekPrev: () => void;
  onSeekRepeat: () => void;
  onSeekNext: () => void;
  onTogglePlayback: () => void;
  onToggleDelayMainMode: () => void;
  onToggleDelaySecondMode: () => void;
  onNudgeDelayMain: (dir: 1 | -1) => void;
  onNudgeDelaySecond: (dir: 1 | -1) => void;
  onTogglePositionMode: () => void;
  onToggleFontSizeMode: () => void;
  onToggleCenterMode?: () => void;
  onTogglePin: () => void;
  onSubYChange: (value: number) => void;
  onFontSizeChange: (value: number) => void;
  onCenterOffsetChange?: (value: number) => void;
  onInteract: () => void;
  labels: {
    seekPrev: string;
    seekRepeat: string;
    seekNext: string;
    play: string;
    pause: string;
    delayMain: string;
    delaySecond: string;
    delayDown: string;
    delayUp: string;
    position: string;
    positionSlider: string;
    fontSize: string;
    fontSizeSlider: string;
    center?: string;
    centerSlider?: string;
    pin: string;
  };
};

function formatDelayMs(ms: number): string {
  const s = Math.round(ms) / 1000;
  const sign = s > 0 ? '+' : '';
  return `${sign}${s.toFixed(1)}s`;
}

/** Invert 0–1 rail values so drag direction matches on-screen motion. */
function invertUnit(n: number): number {
  const v = Number.isFinite(n) ? n : 0;
  return Math.min(1, Math.max(0, Math.round((1 - v) * 100) / 100));
}

function RailButton({
  testId,
  label,
  active,
  onClick,
  children,
}: {
  testId: string;
  label: string;
  active?: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      className={`stage-rails__btn${active ? ' stage-rails__btn--active' : ''}`}
      data-testid={testId}
      aria-label={label}
      aria-pressed={active}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <span aria-hidden="true">{children}</span>
    </button>
  );
}

/**
 * Ephemeral stage rails for watch / learning / shadowing.
 * Landscape: seek left, tools right.
 * Portrait: tools top, seek bottom.
 */
export function StageRails({
  visible,
  toolsMode,
  pinned,
  layout = 'sides',
  subY,
  fontSize,
  centerOffset = 0.5,
  showCenterControl = false,
  mainDelayMs = 0,
  secondDelayMs = 0,
  paused,
  onSeekPrev,
  onSeekRepeat,
  onSeekNext,
  onTogglePlayback,
  onToggleDelayMainMode,
  onToggleDelaySecondMode,
  onNudgeDelayMain,
  onNudgeDelaySecond,
  onTogglePositionMode,
  onToggleFontSizeMode,
  onToggleCenterMode,
  onTogglePin,
  onSubYChange,
  onFontSizeChange,
  onCenterOffsetChange,
  onInteract,
  labels,
}: StageRailsProps) {
  function bumpInteract() {
    onInteract();
  }

  const sliderActive = toolsMode !== 'idle';
  const centerLabel = labels.center ?? 'Center line';
  const centerSliderLabel = labels.centerSlider ?? centerLabel;

  const seekRail = (
    <div className="stage-rails__side stage-rails__side--seek" data-testid="stage-rails-seek">
      <RailButton
        testId="rail-seek-prev"
        label={labels.seekPrev}
        onClick={() => {
          bumpInteract();
          onSeekPrev();
        }}
      >
        ◀
      </RailButton>
      <RailButton
        testId="rail-seek-repeat"
        label={labels.seekRepeat}
        onClick={() => {
          bumpInteract();
          onSeekRepeat();
        }}
      >
        ↺
      </RailButton>
      <RailButton
        testId="rail-seek-next"
        label={labels.seekNext}
        onClick={() => {
          bumpInteract();
          onSeekNext();
        }}
      >
        ▶
      </RailButton>
      <RailButton
        testId="rail-playback"
        label={paused ? labels.play : labels.pause}
        onClick={() => {
          bumpInteract();
          onTogglePlayback();
        }}
      >
        {paused ? '▷' : '❚❚'}
      </RailButton>
    </div>
  );

  const delayTrim = (
    slot: 'main' | 'second',
    delayMs: number,
    onNudge: (dir: 1 | -1) => void,
    onToggle: () => void,
    closeLabel: string,
  ) => (
    <>
      <RailButton
        testId={`rail-delay-${slot}-up`}
        label={labels.delayUp}
        onClick={() => {
          bumpInteract();
          onNudge(1);
        }}
      >
        +
      </RailButton>
      <span
        className="stage-rails__delay-value"
        data-testid={`rail-delay-${slot}-value`}
        aria-live="polite"
      >
        {formatDelayMs(delayMs)}
      </span>
      <RailButton
        testId={`rail-delay-${slot}-down`}
        label={labels.delayDown}
        onClick={() => {
          bumpInteract();
          onNudge(-1);
        }}
      >
        −
      </RailButton>
      <RailButton
        testId={slot === 'main' ? 'rail-delay-main' : 'rail-delay-second'}
        label={closeLabel}
        active
        onClick={() => {
          bumpInteract();
          onToggle();
        }}
      >
        ✓
      </RailButton>
    </>
  );

  const toolsRail = (
    <div
      className={`stage-rails__side stage-rails__side--tools${sliderActive ? ' stage-rails__side--slider' : ''}${toolsMode === 'delayMain' || toolsMode === 'delaySecond' ? ' stage-rails__side--delay' : ''}`}
      data-testid="stage-rails-tools"
    >
      {toolsMode === 'position' ? (
        <>
          <label className="stage-rails__slider-wrap">
            <span className="visually-hidden">{labels.positionSlider}</span>
            <input
              type="range"
              className="stage-rails__slider"
              data-testid="rail-suby-slider"
              min={0}
              max={1}
              step={0.01}
              value={invertUnit(subY)}
              aria-label={labels.positionSlider}
              aria-valuemin={0}
              aria-valuemax={1}
              aria-valuenow={Math.round(subY * 100) / 100}
              onPointerDown={(e) => e.stopPropagation()}
              onChange={(e: FormEvent<HTMLInputElement>) => {
                bumpInteract();
                onSubYChange(invertUnit(Number(e.currentTarget.value)));
              }}
            />
          </label>
          <RailButton
            testId="rail-position"
            label={labels.position}
            active
            onClick={() => {
              bumpInteract();
              onTogglePositionMode();
            }}
          >
            ✓
          </RailButton>
        </>
      ) : toolsMode === 'fontSize' ? (
        <>
          <label className="stage-rails__slider-wrap">
            <span className="visually-hidden">{labels.fontSizeSlider}</span>
            <input
              type="range"
              className="stage-rails__slider"
              data-testid="rail-font-slider"
              min={SHORTCUT_FONT_MIN}
              max={SHORTCUT_FONT_MAX}
              step={SHORTCUT_FONT_STEP}
              value={fontSize}
              aria-label={labels.fontSizeSlider}
              aria-valuemin={SHORTCUT_FONT_MIN}
              aria-valuemax={SHORTCUT_FONT_MAX}
              aria-valuenow={Math.round(fontSize * 100) / 100}
              onPointerDown={(e) => e.stopPropagation()}
              onChange={(e: FormEvent<HTMLInputElement>) => {
                bumpInteract();
                onFontSizeChange(Number(e.currentTarget.value));
              }}
            />
          </label>
          <RailButton
            testId="rail-font-size"
            label={labels.fontSize}
            active
            onClick={() => {
              bumpInteract();
              onToggleFontSizeMode();
            }}
          >
            ✓
          </RailButton>
        </>
      ) : toolsMode === 'center' && showCenterControl ? (
        <>
          <label className="stage-rails__slider-wrap">
            <span className="visually-hidden">{centerSliderLabel}</span>
            <input
              type="range"
              className="stage-rails__slider"
              data-testid="rail-center-slider"
              min={0}
              max={1}
              step={0.01}
              value={invertUnit(centerOffset)}
              aria-label={centerSliderLabel}
              aria-valuemin={0}
              aria-valuemax={1}
              aria-valuenow={Math.round(centerOffset * 100) / 100}
              onPointerDown={(e) => e.stopPropagation()}
              onChange={(e: FormEvent<HTMLInputElement>) => {
                bumpInteract();
                onCenterOffsetChange?.(invertUnit(Number(e.currentTarget.value)));
              }}
            />
          </label>
          <RailButton
            testId="rail-center"
            label={centerLabel}
            active
            onClick={() => {
              bumpInteract();
              onToggleCenterMode?.();
            }}
          >
            ✓
          </RailButton>
        </>
      ) : toolsMode === 'delayMain' ? (
        delayTrim(
          'main',
          mainDelayMs,
          onNudgeDelayMain,
          onToggleDelayMainMode,
          labels.delayMain,
        )
      ) : toolsMode === 'delaySecond' ? (
        delayTrim(
          'second',
          secondDelayMs,
          onNudgeDelaySecond,
          onToggleDelaySecondMode,
          labels.delaySecond,
        )
      ) : (
        <>
          <RailButton
            testId="rail-font-size"
            label={labels.fontSize}
            onClick={() => {
              bumpInteract();
              onToggleFontSizeMode();
            }}
          >
            A
          </RailButton>
          <RailButton
            testId="rail-delay-main"
            label={labels.delayMain}
            onClick={() => {
              bumpInteract();
              onToggleDelayMainMode();
            }}
          >
            M
          </RailButton>
          <RailButton
            testId="rail-delay-second"
            label={labels.delaySecond}
            onClick={() => {
              bumpInteract();
              onToggleDelaySecondMode();
            }}
          >
            S
          </RailButton>
          <RailButton
            testId="rail-position"
            label={labels.position}
            onClick={() => {
              bumpInteract();
              onTogglePositionMode();
            }}
          >
            ⇕
          </RailButton>
          {showCenterControl ? (
            <RailButton
              testId="rail-center"
              label={centerLabel}
              onClick={() => {
                bumpInteract();
                onToggleCenterMode?.();
              }}
            >
              ⇔
            </RailButton>
          ) : null}
          <RailButton
            testId="rail-pin"
            label={labels.pin}
            active={pinned}
            onClick={() => {
              bumpInteract();
              onTogglePin();
            }}
          >
            {pinned ? '◉' : '○'}
          </RailButton>
        </>
      )}
    </div>
  );

  return (
    <div
      className="stage-rails"
      data-testid="stage-rails"
      data-visible={visible ? 'true' : 'false'}
      data-tools-mode={toolsMode}
      data-pinned={pinned ? 'true' : 'false'}
      data-layout={layout}
      aria-hidden={!visible}
    >
      {/*
        Portrait (ends): tools on top, seek on bottom.
        Landscape (sides): seek on left, tools on right.
      */}
      {layout === 'ends' ? (
        <>
          {toolsRail}
          {seekRail}
        </>
      ) : (
        <>
          {seekRail}
          {toolsRail}
        </>
      )}
    </div>
  );
}
